import { useCallback, useMemo, useState } from "react";
import TopMenu from "components/EmployeesList/TopMenu";
import List from "components/common/ListWithMoreOptions";
import Modal from "components/common/ModalFullScreen";
import WorkerCards from "components/common/CardEmployees";
import AlertIcon from "components/icons/AlertIcon";
import useUsers from "graphql/useUsers";
import useUserMutations from "graphql/useUserMutations";
import Loading from "components/common/loading";
import { useMessaging } from "context/MessagingContext";
import { buildCustomerName } from "helpers/utils";
import { useTranslation } from "react-i18next";
import moment from "moment";
import BottomPageSelectorWithPagination, {
  usePagination,
} from "../../components/common/BottomPageSelectorWithPagination";
import { usePaginatedData } from "../../helpers/hooks";
import AddWorker from "./FormEmployees";
import s from "./styles";

const SCAPE_VALUE = "\\";

function fixValueForQuery(value) {
  return value
    .replace(`${SCAPE_VALUE}`, `${SCAPE_VALUE}${SCAPE_VALUE}`)
    .replace("*", `${SCAPE_VALUE}*`);
}

function EmployeesList() {
  const { t } = useTranslation();
  const [addWorkerModalOpen, setAddWorkerModalOpen] = useState(false);
  const [viewType, setViewType] = useState("list");
  const [workerToEdit, setWorkerToEdit] = useState(null);

  const { setInfoMessage, setConfirmMessage, setErrorMessage } = useMessaging();

  const {
    sorting,
    setSorting,
    filter,
    setFilter,
    search,
    setSearch,
    pagination,
    setPagination,
  } = usePagination("name");

  const [rolViewList, setRolViewList] = useState("all");

  function changeSearch(v) {
    setSearch(v);
    setPagination({ ...pagination, page: 0 });
  }

  const { users, loading: loading1 } = useUsers();
  const { deleteUser } = useUserMutations();

  const data = useMemo(() => {
    const usersWithSingleRole = users.map(user => {
      const rol = user?.roles?.[0] ? user.roles[0] : null;
      return {
        ...user,
        roles: rol
      };
    });
  
    return usersWithSingleRole.filter((i) => {
      const reg = new RegExp(fixValueForQuery(search), "i");
      return (
        (rolViewList === "all" || i.roles === rolViewList) &&
        ((i.name && i.name.match(reg)) ||
          (i.lastName && i.lastName.match(reg)) ||
          (i.name && i.lastName && `${i.name} ${i.lastName}`.match(reg)) ||
          (i.email && i.email.match(reg)) ||
          (i.phone && i.phone.match(reg)) ||
          (i.roles && i.roles.toLowerCase().includes(search)))
      );
    });
  }, [users, rolViewList, search]);
  

  const loading = loading1;

  const dataToShow = usePaginatedData(
    data,
    pagination,
    sorting,
    (worker) => [
      [worker.name, " ", worker.lastName],
      worker.phone,
      worker.email,
      worker.roles ? t(`containers.EmployeesList.${worker.roles}`) : "-",
      worker.lastLogin ? moment(worker.lastLogin).format("lll") : "-",
      moment(worker.updateDatetime).format("lll"),
    ],
    {
      roles: (a, b) => a.roles.localeCompare(b.roles),
    }
  );

  const onUpdateSuccess = useCallback(() => {
    setInfoMessage({
      titleAlert: t("containers.EmployeesList.workerEditMessage"),
      info1: t("containers.EmployeesList.workerEditedMessage"),
      info2: t("containers.EmployeesList.workerFindMessage"),
    });
  }, []);
  const onAddSuccess = useCallback(() => {
    setInfoMessage({
      titleAlert: t("containers.EmployeesList.workerAddedMessage"),
      info1: t("containers.EmployeesList.workerReadyMessage"),
      info2: t("containers.EmployeesList.workerFindMessage"),
    });
  }, []);

  const noDelete = useCallback(() => {
    setInfoMessage({
      titleAlert: t("containers.EmployeesList.noDeleteRoot"),
      info1: t("containers.EmployeesList.noDeleteRootMessage"),
    });
  }, []);

  const onDeleteRequest = useCallback((worker) => {
    setConfirmMessage({
      titleAlert: t("containers.EmployeesList.delete"),
      confirm1: `${t("containers.EmployeesList.deleteUser")}
      
      ${buildCustomerName(worker.element)}?`,
      confirm2: t("containers.EmployeesList.deleteUserUndone"),
      onConfirm: () =>
        deleteUser(worker.element)
          .then(() => {
            setAddWorkerModalOpen(false);
          })
          .catch((e) => setErrorMessage({ error: e })),
    });
  }, []);
  return (
    <s.PageContainer>
      <TopMenu
        viewType={viewType}
        setviewType={setViewType}
        selectedFilter={filter}
        changeSelectedFilter={(v) => setFilter(v)}
        changeSearchValue={(v) => changeSearch(v)}
        setrolViewList={setRolViewList}
        addWorker={() => setAddWorkerModalOpen(true)}
        loading={loading}
      />
      {loading ? (
        <Loading />
      ) : (
        <>
          <s.ListContainer>
            {/* show no content yet */}
            {users.length === 0 && (
              <s.NoWorkersContainer>
                <s.NoWorkersMessageCenter>
                  <AlertIcon
                    styles={{ display: "block", marginBottom: "7px" }}
                  />
                  {t("containers.EmployeesList.noItems")}

                  <s.NoWorkersMessageSecond>
                    {t("containers.EmployeesList.noItemsSuggestion")}
                  </s.NoWorkersMessageSecond>
                </s.NoWorkersMessageCenter>
              </s.NoWorkersContainer>
            )}

            {viewType === "list" && users.length > 0 ? (
              <List
                width="1200px"
                handleEdit={(item) => {
                  setWorkerToEdit(item.element);
                  setAddWorkerModalOpen(true);
                }}
                handleDeleteWorker={(item) => {
                  if (item.element.roles[0] === "root") {
                    noDelete();
                  } else {
                    onDeleteRequest(item);
                  }
                }}
                data={dataToShow}
                columns={[
                  {
                    title: t("containers.EmployeesList.name"),
                    sortable: true,
                    columnId: "name",
                  },
                  {
                    title: t("containers.EmployeesList.phone"),
                    sortable: true,
                    columnId: "phone",
                  },
                  {
                    title: t("containers.EmployeesList.email"),
                    sortable: true,
                    columnId: "email",
                  },
                  {
                    title: t("containers.EmployeesList.role"),
                    sortable: true,
                    columnId: "roles",
                    dataIndex: "roles",
                    key: "roles",
                  },
                  {
                    title: t("containers.EmployeesList.lastConnet"),
                    sortable: true,
                    columnId: "updateDatetime",
                  },
                  {
                    title: t("containers.EmployeesList.updateDatetime"),
                    sortable: true,
                    columnId: "updateDatetime",
                  },
                  {
                    title: "",
                  },
                ]}
                sorting={sorting}
                changeSorting={(columnId, order) =>
                  setSorting({ columnId, order })
                }
                tableStyle={{
                  gridTemplateColumns: "23fr 16fr 17fr 13fr 13fr 13fr 6fr",
                }}
              />
            ) : (
              viewType === "thumbnails" &&
              users.length > 0 && (
                <WorkerCards
                  workers={data}
                  setWorkerToEdit={setWorkerToEdit}
                  onDeleteRequest={onDeleteRequest}
                  setAddWorkerModalOpen={setAddWorkerModalOpen}
                />
              )
            )}
          </s.ListContainer>
          <BottomPageSelectorWithPagination
            pagination={pagination}
            setPagination={setPagination}
            length={data.length}
          />
        </>
      )}

      {addWorkerModalOpen && (
        <Modal>
          <AddWorker
            closeModal={() => {
              setAddWorkerModalOpen(false);
              setWorkerToEdit(null);
            }}
            editItem={workerToEdit}
            onUpdateSuccess={onUpdateSuccess}
            onAddSuccess={onAddSuccess}
            onDeleteRequest={onDeleteRequest}
            setErrorMessage={setErrorMessage}
          />
        </Modal>
      )}
    </s.PageContainer>
  );
}

export default EmployeesList;
